import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomText, { textStyles } from "../../components/CustomText";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";



// Icon imports
import SpaIcon from "@mui/icons-material/Spa";
import ElderlyIcon from "@mui/icons-material/Elderly";
import AccessibleIcon from "@mui/icons-material/Accessible";
import ChurchIcon from "@mui/icons-material/Church";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SchoolIcon from "@mui/icons-material/School";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HomeIcon from "@mui/icons-material/Home";
import HotelIcon from "@mui/icons-material/Hotel";
import BiotechIcon from "@mui/icons-material/Biotech";
import DeleteIcon from "@mui/icons-material/Delete";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import BuildIcon from "@mui/icons-material/Build";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StoreIcon from "@mui/icons-material/Store";
import SecurityIcon from "@mui/icons-material/Security";
import PetsIcon from "@mui/icons-material/Pets";

const industries = [
  { title: "Alternative Health", icon: <SpaIcon /> },
  { title: "Assisted Living", icon: <ElderlyIcon /> },
  { title: "Chiropractors", icon: <AccessibleIcon /> },
  { title: "Churches", icon: <ChurchIcon /> },
  { title: "Cleaning Companies", icon: <CleaningServicesIcon /> },
  { title: "Dentists", icon: <MedicalServicesIcon /> },
  { title: "Education", icon: <SchoolIcon /> },
  { title: "Fitness Centers", icon: <FitnessCenterIcon /> },
  { title: "Franchises", icon: <StoreMallDirectoryIcon /> },
  { title: "Health & Wellness", icon: <HealthAndSafetyIcon /> },
  { title: "Healthcare", icon: <LocalHospitalIcon /> },
  { title: "Home Healthcare", icon: <HomeIcon /> },
  { title: "Hospitality", icon: <HotelIcon /> },
  { title: "Imaging & Diagnostics", icon: <BiotechIcon /> },
  { title: "Janitorial", icon: <DeleteIcon /> },
  { title: "Manufacturing", icon: <PrecisionManufacturingIcon /> },
  { title: "Mechanics", icon: <BuildIcon /> },
  { title: "Medical Practices", icon: <VaccinesIcon /> },
  { title: "Mental Health", icon: <PsychologyIcon /> },
  { title: "Optometrists", icon: <VisibilityIcon /> },
  { title: "Pharmacies", icon: <LocalPharmacyIcon /> },
  { title: "Restaurants", icon: <RestaurantIcon /> },
  { title: "Retail", icon: <StoreIcon /> },
  { title: "Security", icon: <SecurityIcon /> },
  { title: "Veterinarians", icon: <PetsIcon /> }
];

const OnSiteWorkers = () => {
  const CARDS_PREVIEW_COUNT = 6;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const visibleIndustries = isMobile && !showAll ? industries.slice(0, CARDS_PREVIEW_COUNT) : industries;

  return (
    <Box sx={{ px: 3, py: 6, mt: 1, textAlign: "center" }}>
      <CustomText
        style={{
          ...textStyles.h1,
          marginBottom: isMobile ? "0rem" : "1rem",
          marginTop: isMobile ? "0rem" : "1rem",
          fontSize: isMobile ? "30px" : "36px",
          textAlign: "center",
        }}
      >
        {t("onsite.title")}
      </CustomText>

      <CustomText
        style={{
          ...textStyles.body1,
          color: "#868282",
          maxWidth: "910px",
          margin: "0 auto",
          marginTop: "10px",
          fontSize: "24px",
          lineHeight: "120%",
          textAlign: "center",
        }}
      >
        {t("onsite.description")}
      </CustomText>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: { xs: 2, md: 3 },
          px: { xs: 1, sm: 5, lg: 28 },
          mt: 3,
        }}
      >
        {visibleIndustries.map((item, index) => (
          <Box
            key={index}
            sx={{
              width: { xs: "46%", md: "30%" },
              minHeight: { xs: 100, md: 120 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #DFDFDF",
              borderRadius: "12px",
              p: { xs: 1, md: 2 },
              cursor: "pointer",
              transition: "0.3s",
              "&:hover": {
                transform: "scale(1.03)",
              },
            }}
          >
            <Box sx={{ color: "#1976d2", mb: 0.2 }}>{item.icon}</Box>
            <Typography sx={{ fontWeight: 700, color: "#003149", fontSize: { xs: "13px", md: "18px" }, textAlign: "center" }}>
              {t(`onsite.industries.${item.title}`)}
            </Typography>
          </Box>
        ))}
      </Box>

      {isMobile && industries.length > CARDS_PREVIEW_COUNT && (
        <Box
          onClick={() => setShowAll((prev) => !prev)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: 4,
            cursor: "pointer",
            color: "#1976d2",
            fontWeight: 600,
            fontSize: "16px",
            userSelect: "none",
            "&:hover": { opacity: 0.75 },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "inherit" }}>
            {showAll ? t("common.viewLess") : t("common.viewMore")}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              transition: "transform 0.3s",
              transform: showAll ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OnSiteWorkers;
